import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BrandLockup, BrandMark } from '../../atoms/Brand/Brand.js';
import { Tooltip } from '../../atoms/Tooltip/Tooltip.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING, FOCUS_RING_INSET, FOCUS_RING_ON_DARK } from '../../lib/focus-ring.js';
import { useTranslate } from '../../lib/i18n.js';
import { useViewportInset } from '../../lib/viewport-inset.js';
/*
 * The application frame, as parts.
 *
 * `[sidebar 260px] [header + work area]` above 1119px, a 72px icon rail from
 * 768 to 1119, and a bottom bar below 768. The product and the administration
 * both have that frame and used to write it out twice, class for class, so a
 * change to one — a focus ring, a rail width — quietly missed the other.
 *
 * These are primitives rather than one configurable shell on purpose. The two
 * applications agree on the frame and disagree on what goes in it: the
 * product's header holds the page title and a search, the administration's
 * says where you are and holds nothing else; a tutor's nav is roomy, an
 * administrator's is a tool. A single `<AppShell isAdmin compact noSearch>`
 * would be those differences written as flags. Here each app composes the
 * parts and keeps its own content.
 *
 * The router owns the anchors — TanStack's `Link` has to render the element
 * to handle navigation — so the item parts are class functions and the
 * content that goes inside a link, the same arrangement `buttonClasses` and
 * `linkClasses` have.
 */
/** The work area's id: where the skip link sends focus. */
export const MAIN_CONTENT_ID = 'main-content';
/** The page, the offset the fixed sidebar needs, and the two navigation forms. */
export function AppFrame({ sidebar, bottomBar, header, mainClassName, children }) {
    return (_jsxs("div", { className: "min-h-dvh bg-background", children: [_jsx(SkipLink, {}), sidebar, _jsxs("div", { className: "md:pl-sidebar-rail desktop:pl-sidebar", children: [header, _jsx("main", { id: MAIN_CONTENT_ID, tabIndex: -1, className: cn(mainClassName, 'outline-none', 
                        // The bottom bar's own height, whatever its labels made it, so the
                        // last thing on a page is never underneath it.
                        'max-md:pb-[calc(var(--app-inset-bottom,0px)+1.5rem)]'), children: children })] }), bottomBar] }));
}
/**
 * "Skip to content", the first Tab stop in a shell.
 *
 * Out of sight until it has keyboard focus. Every screen puts the brand, up to
 * five destinations and a pinned action ahead of its own content, and a
 * keyboard user pays for those on every page; this pays once.
 *
 * It moves focus itself rather than following its `href`. A fragment
 * navigation is a history entry the router would treat as a route change, and
 * the `href` is only there so the element is a link.
 */
export function SkipLink({ targetId = MAIN_CONTENT_ID }) {
    const t = useTranslate();
    return (_jsx("a", { href: `#${targetId}`, onClick: (event) => {
            const target = document.getElementById(targetId);
            if (!target)
                return;
            event.preventDefault();
            target.focus();
        }, className: cn('sr-only rounded-sm bg-surface text-body-sm font-semibold text-primary underline', 
        // `not-sr-only` resets padding to 0, so the padding comes after it:
        // a 44px target, on screen only while it has focus.
        'focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-30 focus:shadow-elevation-2', 'focus:inline-flex focus:min-h-touch-target-min focus:items-center focus:px-4', FOCUS_RING), children: t('navigation.skipToContent') }));
}
/**
 * The sticky header across the top of the work area.
 *
 * The frame's rather than each app's because of what it has to tell the page:
 * how tall it is. It publishes that as `--app-inset-top`, so a control that
 * takes focus while scrolled up is scrolled out from under it — and it is
 * rarely one height: the tutor's header is two rows on a phone.
 */
export function AppHeader({ className, children }) {
    const inset = useViewportInset('top');
    return (_jsx("header", { ref: inset, className: cn('sticky top-0 z-10 border-b border-border bg-surface px-4 md:px-6', className), children: children }));
}
/** The indigo sidebar. Hidden on phones, where the bottom bar takes over. */
export function AppSidebar({ label, children }) {
    return (_jsx("nav", { "aria-label": label, className: cn(
        // Clear of a landscape phone's notch; 0 everywhere else.
        'fixed inset-y-0 left-[env(safe-area-inset-left)] z-20 hidden flex-col gap-2 bg-primary-dark p-4', 'md:flex md:w-sidebar-rail desktop:w-sidebar', 
        // Indigo, so every focus ring in here turns to the accent — the
        // brand, a pinned action and each nav item alike.
        FOCUS_RING_ON_DARK), children: children }));
}
/** The brand link at the top of the sidebar. */
export const sidebarBrandClasses = cn('mb-4 flex h-control-lg items-center justify-center rounded-sm desktop:justify-start desktop:px-2', FOCUS_RING);
/**
 * What the sidebar's brand link shows: the mark on the rail, the lockup in
 * the full sidebar, both in their on-dark artwork. The artwork is
 * `aria-hidden`, so the link's name is `label`.
 */
export function SidebarBrand({ label }) {
    return (_jsxs(_Fragment, { children: [_jsx(BrandMark, { tone: "on-dark", className: "h-8 desktop:hidden" }), _jsx(BrandLockup, { tone: "on-dark", className: "hidden h-8 desktop:block" }), _jsx("span", { className: "sr-only", children: label })] }));
}
/**
 * A sidebar destination, on the indigo sidebar (`primary-dark`).
 *
 * The one navigation item in the product, so its colours are the dark
 * surface's: rest is `primary-border` on indigo, hover lifts the row with a
 * translucent `primary`, and the current place is the light `primary-light`
 * pill with `primary-dark` text — a shape and a weight as well as a colour.
 * Focus rings inside the sidebar turn to the accent (`FOCUS_RING_ON_DARK` on
 * `AppSidebar`). Forced colours repaints the pill away, so there the current
 * place takes the system highlight.
 *
 * `compact` is the administration's: a 48px row and small text, because an
 * operational tool's navigation is a list of places to go, not the product's
 * front door. `comfortable` is the product's 52px row.
 */
export function sidebarItemClasses(active, density = 'comfortable') {
    return cn('flex items-center justify-center rounded-sm transition-colors duration-fast desktop:justify-start', density === 'compact'
        ? 'h-control-lg gap-3 px-2 text-body-sm'
        : 'h-[52px] gap-[14px] px-[18px] text-body', active
        ? cn('bg-primary-light font-semibold text-primary-dark', 'forced-colors:bg-[Highlight] forced-colors:text-[HighlightText] forced-colors:forced-color-adjust-none')
        : 'font-medium text-primary-border hover:bg-primary/40 hover:text-surface', FOCUS_RING);
}
/** Inside a sidebar destination: the icon, and a label only the full sidebar shows. */
export function SidebarItemContent({ icon, label }) {
    return (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", className: "flex size-icon-md shrink-0 items-center justify-center", children: icon }), _jsx("span", { className: "hidden desktop:inline", children: label }), _jsx("span", { className: "sr-only desktop:hidden", children: label })] }));
}
/**
 * The destination's label, beside it, on the icon rail.
 *
 * Between 768 and 1119 the sidebar is 72px and a destination is only its
 * icon. A users icon reads as "accounts" to most people; an inbox or a
 * library icon does not reliably read as anything. The link already has its
 * name for assistive technology — this is for sighted people, on hover and
 * on keyboard focus, and it steps aside at desktop, where the label is
 * written out next to the icon.
 */
export function SidebarTooltip({ label, children }) {
    return (_jsx(Tooltip, { content: label, side: "right", contentClassName: "desktop:hidden", children: children }));
}
/**
 * The brand link in the header, on phones only, where the sidebar that carries it is gone.
 *
 * A 44px target around a 28px mark, pulled back into the space the mark took
 * by negative margins, so a thumb has a whole target and the header keeps its
 * height and alignment.
 */
export const headerBrandClasses = cn('flex size-touch-target-min shrink-0 items-center justify-center rounded-sm md:hidden', '-mx-2 -my-2', FOCUS_RING);
export function HeaderBrand({ label }) {
    return (_jsxs(_Fragment, { children: [_jsx(BrandMark, { className: "h-7" }), _jsx("span", { className: "sr-only", children: label })] }));
}
/**
 * The phone navigation, fixed to the bottom of the screen.
 *
 * It publishes its height as `--app-inset-bottom`, which the work area pads
 * by, focus scrolls clear of, and toasts sit above. The safe-area padding
 * keeps it off a phone's home indicator; it is 0 on a screen without one.
 */
export function AppBottomBar({ label, children }) {
    const inset = useViewportInset('bottom');
    return (_jsx("nav", { ref: inset, "aria-label": label, className: cn('fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-surface md:hidden', 'pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]'), children: children }));
}
export function bottomBarItemClasses(active) {
    return cn('group flex min-h-touch-target-min flex-1 flex-col items-center justify-center gap-1 py-1.5', 
    // min-w-0 and px-1 are for the label, not the icon: a flex item defaults
    // to `min-width: auto`, so a label longer than its track pushes the bar
    // sideways instead of wrapping inside it. "Estudiantes" is eleven
    // characters where "Students" is eight, and there are four of these
    // across a phone.
    'min-w-0 px-1', 'text-center text-caption leading-tight text-balance', active ? 'font-semibold text-primary-dark' : 'font-medium text-text-secondary', 
    // Inset: this sits flush to the viewport, where an outward offset would
    // be clipped rather than drawn.
    FOCUS_RING_INSET);
}
/**
 * A bottom-bar destination's icon and label.
 *
 * The current one sits on a tinted pill. Indigo against grey is the only
 * other difference, and a hue is not a state: someone who cannot tell those
 * two apart, or is in forced-colours mode where both become one link colour,
 * still sees which place they are in. Forced colours paints the pill in the
 * system's own highlight.
 */
export function BottomBarItemContent({ icon, label }) {
    return (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", className: cn('flex h-6 w-12 shrink-0 items-center justify-center rounded-full transition-colors', '[&>svg]:size-icon-md', 'group-aria-[current=page]:bg-primary-light', 'forced-colors:group-aria-[current=page]:bg-[Highlight] forced-colors:group-aria-[current=page]:text-[HighlightText]', 'forced-colors:group-aria-[current=page]:forced-color-adjust-none'), children: icon }), label] }));
}
//# sourceMappingURL=AppFrame.js.map